import firebase, { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";
import {
  CommunityMediaConfig,
  CompetitorReferenceAd,
  AdPlatform,
} from "../../types/CommunityMediaConfig";
import { getCommunityMediaConfig } from "./mediaConfig";
import {
  deleteMedia,
  uploadMedia,
} from "../media/cloudflareMedia";

const mediaConfigRef = (communityId: string) =>
  firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.COMMUNITY_MEDIA)
    .doc("mediaConfig");

const guessExtension = (file: File): string => {
  if (file.type === "image/png") return "png";
  if (file.type === "image/jpeg") return "jpg";
  if (file.type === "image/webp") return "webp";
  const dot = file.name.lastIndexOf(".");
  if (dot >= 0) return file.name.slice(dot + 1).toLowerCase();
  return "png";
};

export type UploadCompetitorReferenceInput = {
  communityId: string;
  file: File;
  label?: string;
  source?: string;
  platform?: AdPlatform;
};

/**
 * Uploads a competitor ad screenshot to Storage and appends it to the
 * community's mediaConfig.competitorReferenceAds array.
 */
export const uploadCompetitorReferenceAd = async (
  input: UploadCompetitorReferenceInput
): Promise<CompetitorReferenceAd> => {
  const communityId = (input.communityId || "").trim();
  if (!communityId) throw new Error("communityId is required.");
  if (!input.file) throw new Error("file is required.");

  const id = firestore.collection("_").doc().id;
  const ext = guessExtension(input.file);
  const storagePath = `Community/${communityId}/CompetitorReferenceAds/${id}.${ext}`;
  const uploaded = await uploadMedia({
    body: input.file,
    kind: "image",
    fileName: input.file.name,
    firebasePath: storagePath,
  });
  const imageURL = uploaded.url;

  const entry: CompetitorReferenceAd = {
    id,
    imageURL,
    ...(input.label?.trim() ? { label: input.label.trim() } : {}),
    ...(input.source?.trim() ? { source: input.source.trim() } : {}),
    ...(input.platform ? { platform: input.platform } : {}),
    createdAt: new Date().toISOString(),
  };

  // Read-modify-write so we don't overwrite concurrent additions.
  const ref = mediaConfigRef(communityId);
  const snap = await ref.get();
  const existing =
    ((snap.data() || {}) as { competitorReferenceAds?: CompetitorReferenceAd[] })
      .competitorReferenceAds || [];

  await ref.set(
    { competitorReferenceAds: [...existing, entry] },
    { merge: true }
  );

  return entry;
};

export const deleteCompetitorReferenceAd = async (
  communityId: string,
  entryId: string
): Promise<void> => {
  if (!communityId || !entryId) return;
  const ref = mediaConfigRef(communityId);
  const snap = await ref.get();
  const existing =
    ((snap.data() || {}) as { competitorReferenceAds?: CompetitorReferenceAd[] })
      .competitorReferenceAds || [];
  const target = existing.find((e) => e.id === entryId);
  const remaining = existing.filter((e) => e.id !== entryId);

  await ref.set({ competitorReferenceAds: remaining }, { merge: true });

  if (target?.imageURL) {
    try {
      await deleteMedia({ url: target.imageURL });
    } catch (e) {
      console.warn("Failed to delete competitor reference image:", e);
    }
  }
};

export const listCompetitorReferenceAds = async (
  communityId: string
): Promise<CompetitorReferenceAd[]> => {
  if (!communityId) return [];
  const cfg: CommunityMediaConfig = await getCommunityMediaConfig(communityId);
  return cfg.competitorReferenceAds || [];
};

/**
 * Fetches a public Storage URL and converts it to a data URL so it can be
 * passed inline to gpt-image-2 / Gemini image APIs (they both need base64).
 */
export const fetchImageAsDataUrl = async (url: string): Promise<string> => {
  if (!url) return "";
  if (url.startsWith("data:")) return url;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch reference image: ${url}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
};
